import { useState, useCallback } from 'react';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useCommonModal } from 'components/CommonModal';
import { urls } from 'constants/urls';
import { navigateTo } from 'utils/location';
import { useShoppingContext } from 'contexts/ShoppingContext';
import { useSlotContext } from 'contexts/Slot';
import { useTrolley } from 'contexts/Trolley';
import orderOrchestrationServiceClient from 'services/orderOrchestration';
import { pathJoin } from 'utils/general';
import { CommonModalProps } from 'components/CommonModal/context';
import { PlaceOrder409Error } from 'services/orderOrchestration/types';
import { sendInstantCheckoutTransactionCompleteEvent } from './sendInstantCheckoutTransactionCompleteEvent';

const useInstantCheckout = (
  location: string,
  getInstantCheckoutErrorModal: (error: PlaceOrder409Error, location?: string) => CommonModalProps,
) => {
  const {
    shoppingContext: { customerOrderId },
  } = useShoppingContext();
  const {
    state: { token, tokenServiceCsrf },
  } = useAuthentication();
  const { trackEvent } = useAnalytics();
  const { openModal } = useCommonModal();
  const { state: trolleyState } = useTrolley();
  const { slotContext } = useSlotContext();
  const [loading, setLoading] = useState(false);

  const handleInstantCheckout = useCallback(async () => {
    trackEvent({
      event: 'instant_checkout_click',
      instant_checkout: {
        location,
      },
    });
    setLoading(true);
    const response = await orderOrchestrationServiceClient.placeOrder({
      authHeader: token!.authHeader,
      customerOrderId,
      orderOptions: { instantCheckout: true },
    });

    if (!response.error) {
      sendInstantCheckoutTransactionCompleteEvent(
        trolleyState.trolley?.trolleyItems,
        slotContext.details?.slotType,
        response.order,
        tokenServiceCsrf,
      );
      navigateTo(pathJoin(urls.orderConfirmation, customerOrderId));
    } else {
      openModal(getInstantCheckoutErrorModal(response.error, location));
    }

    return response;
  }, [
    customerOrderId,
    getInstantCheckoutErrorModal,
    location,
    openModal,
    slotContext,
    token,
    trackEvent,
    trolleyState,
    tokenServiceCsrf,
  ]);
  return { handleInstantCheckout, loading };
};

export default useInstantCheckout;
